import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL = 15000; // 15 seconds

export function NotificationBell({ token }) {
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/notifications/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCount(res.data.count);
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        const res = await axios.get(`${API_URL}/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifications(res.data);
        // Mark all as read
        await axios.put(`${API_URL}/notifications/mark-read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCount(0);
      } catch {}
    }
  };

  const typeIcon = (type) => {
    if (type === 'lesson') return '📚';
    if (type === 'online-live') return '🔴';
    if (type === 'online') return '🎥';
    return '✉️';
  };

  const timeAgo = (isoStr) => {
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Сега';
    if (mins < 60) return `преди ${mins} мин`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `преди ${hrs} ч`;
    return `преди ${Math.floor(hrs / 24)} дни`;
  };

  return (
    <div ref={dropdownRef} className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleOpen}
        data-testid="notification-bell-btn"
        className="relative p-2 hover:bg-slate-100 rounded-full"
      >
        <Bell className="h-5 w-5 text-slate-600" />
        {count > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </Button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Нотификации</span>
            {notifications.length > 0 && (
              <span className="text-xs text-slate-400">{notifications.length} общо</span>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">
                Няма нотификации
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.notification_id}
                  data-testid="notification-item"
                  className="px-4 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-base mt-0.5 flex-shrink-0">{typeIcon(n.type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-800 line-clamp-2">{n.title}</p>
                      {n.body && (
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                      )}
                      <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
